/**
 * useRewardPledges — Manages reward pledges (sending, receiving, claiming).
 */
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RewardPledge } from '@/types/rewards';
import { useToast } from '@/hooks/use-toast';
import { usePledgeMutations, PledgeMutationsContext } from '@/hooks/reward-pledges/usePledgeMutations';
import { usePledgeQueries } from '@/hooks/reward-pledges/usePledgeQueries';
import { usePledgesState } from '@/hooks/reward-pledges/usePledgesState';
import { logger } from '@/utils/logger';

export function useRewardPledges() {
  const toast = useToast();
  const { 
    sentPledges, 
    setSentPledges,
    receivedPledges, 
    setReceivedPledges,
    loading, 
    setLoading,
    error, 
    setError
  } = usePledgesState();
  
  // Create context for queries
  const pledgeQueriesContext = {
    sentPledges,
    receivedPledges,
    setSentPledges,
    setReceivedPledges,
    setError,
    setLoading,
    toast
  };
  
  const { fetchPledges: queryFetchPledges, getProofsForPledge } = usePledgeQueries(pledgeQueriesContext);
  
  const fetchPledges = useCallback(async () => {
    await queryFetchPledges();
  }, [queryFetchPledges]);
  
  const mutationsContext: PledgeMutationsContext = {
    toast,
    fetchPledges
  };
  
  const { createPledge, submitProof, reviewProof, cancelPledge, cleanupDuplicates, acceptPledge, fulfillPledge, markPledgeDone, approvePledge, rejectPledge } = usePledgeMutations(mutationsContext);
  
  return {
    sentPledges,
    receivedPledges,
    loading,
    error,
    fetchPledges,
    createPledge,
    submitProof,
    reviewProof,
    getProofsForPledge,
    cancelPledge,
    cleanupDuplicates,
    acceptPledge,
    fulfillPledge,
    markPledgeDone,
    approvePledge,
    rejectPledge,
  };
}
